/**
 * EnergyCard — the right-column energy-certificate card (Figma 192:97), moved
 * here from the main property column. Shows the A–E chart with the rating (or
 * "Pending"), then the CO2 and Energy Consumption rows with coloured letter
 * badges (mirrors production AccordionFER.js: CO2 = RsCO2Rating[Letter],
 * consumption = RsEnergyRating[Letter]).
 *
 * Props: offer (uses offer.energy = { pending, ratingLetter, co2Letter, co2,
 *               consumptionLetter, consumption })
 */
import React from 'react';
import { useStrings } from './i18n';
import { asset } from './assets';

// 2-decimal number, or null when there's nothing to show.
const fmt = (n) => (n > 0
    ? n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : null);

// Bundled certificate art only covers A–E, but Spanish EPC ratings run A–G, so
// an F/G property used to render the "not rated" chart. Preference order:
//   1. the feed's own rendered certificate (EnergyRatingMedia) — correct for
//      every letter, and drawn from the property's real values;
//   2. the bundled A–E SVG;
//   3. the "not rated" placeholder (pending / unrated / unknown letter).
const RATING_LETTERS = ['A', 'B', 'C', 'D', 'E'];
function certAsset(e) {
    const L = String(e.ratingLetter || '').toUpperCase();
    if (!e.pending && RATING_LETTERS.indexOf(L) !== -1) {
        return `prototype/energy-cert-${L.toLowerCase()}.svg`;
    }
    return 'prototype/energy-cert-nr.svg';
}

// The certificate image to render: the feed's rendered one when the property is
// rated and the feed supplied it, else the bundled asset.
function certSrc(e) {
    return (!e.pending && e.certUrl) ? e.certUrl : asset(certAsset(e));
}

// Coloured A–G rating letter badge.
function ELetter({ letter }) {
    if (!letter) return null;
    const L = String(letter).toUpperCase();
    return <span className={`rs-eletter rs-eletter--${L}`}>{L}</span>;
}

export default function EnergyCard({ offer }) {
    const t = useStrings();
    const e = offer.energy || {};
    const co2 = fmt(e.co2);
    const consumption = fmt(e.consumption);
    // Pending, or no rating letter and no CO2/consumption figures = nothing real
    // to chart. The static energy-cert.svg is then just a placeholder, hidden on
    // mobile.
    const hasData = !e.pending && !!(e.ratingLetter || co2 || consumption);
    return (
        <div className="rs-energy-card">
            <p className="rs-energy-card__h">
                {t.energyCertificate} {e.pending ? t.pending : (e.ratingLetter || '')}
            </p>
            <img
                className={`rs-energy-card__chart${hasData ? '' : ' rs-energy-card__chart--empty'}`}
                src={certSrc(e)}
                alt={t.energyCertificate}
            />

            <div className="rs-energy-card__row">
                <span className="rs-energy-card__k">{t.co2}</span>
                <span className="rs-energy-card__v">
                    <ELetter letter={e.co2Letter} /> {co2 ? `${co2} ${t.co2PerYear}` : t.notKnown}
                </span>
            </div>
            <div className="rs-energy-card__row">
                <span className="rs-energy-card__k">{t.energyConsumption}</span>
                <span className="rs-energy-card__v">
                    <ELetter letter={e.consumptionLetter} /> {consumption ? `${consumption} ${t.kwhPerYear}` : t.notKnown}
                </span>
            </div>
        </div>
    );
}
